import { useQueryClient } from '@tanstack/react-query';
import type { FuseResultMatch } from 'fuse.js';
import { useEffect, useState } from 'react';
import { friendsQueryOptions } from '@/hooks/contacts/use-friends';
import { groupsQueryOptions } from '@/hooks/contacts/use-groups';
import type { ForwardTarget } from '@/hooks/forward/use-batch-forward';
import useConversationSessions, { allChatSessionQueryOptions } from '@/hooks/sessions/use-conversation-sessions';
import useTags from '@/hooks/tags/use-tags';
import { generateSessionKey } from '@/utils/session';
import useFuseSearch from '../search/use-fuse-search';
import useContacts from './use-contacts';
import useSortedFriends from './use-sorted-friends';

export type ForwardListItem = {
  imageURL?: string;
  order?: number;
  userCount?: number;
} & ForwardTarget & { FuzzySearch?: { highlightRanges: readonly FuseResultMatch[] | undefined; score?: number } };

export type ForwardListType = 'recent' | 'friends' | 'groups' | 'tags';

export default function useForwardList(defaultType: ForwardListType = 'friends') {
  const queryClient = useQueryClient();
  const { groups } = useContacts();
  const { availableSessions } = useConversationSessions();
  const { tags } = useTags();
  const { friends, sortedFriends, sortBy, setSortBy } = useSortedFriends();
  const [type, setType] = useState<ForwardListType>(defaultType);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredRecentSessions = useFuseSearch({
    data: availableSessions
      .filter((s) => s.SessionType === 'session' || s.GroupType === 0)
      .map((s) => ({
        ...s,
        RemarkName: s.SessionType === 'session' && friends[s.UserID]?.RemarkName,
      })),
    keys: ['NickName', 'RemarkName', 'GroupName'],
    initialReturnAll: true,
    keyword: searchTerm,
    mapResultItem: ({ item, matches, score }): ForwardListItem => {
      if (item.SessionType === 'group') {
        return {
          key: generateSessionKey('group', item.ID),
          type: 'group',
          groupId: item.ID,
          imageURL: item.GroupImg,
          name: item.GroupName,
          userCount: item.UserCount,
          FuzzySearch: { highlightRanges: matches, score },
        };
      }
      return {
        key: generateSessionKey('session', item.ID),
        type: 'user',
        imageURL: item.HeadImg,
        remarkName: item.RemarkName || '',
        userId: item.UserID,
        name: item?.RemarkName || item?.NickName,
        FuzzySearch: { highlightRanges: matches, score },
      };
    },
  });

  const filteredGroups = useFuseSearch({
    data: Object.values(groups).filter((g) => g.GroupType === 0),
    keys: ['GroupName'],
    initialReturnAll: true,
    keyword: searchTerm,
    mapResultItem: ({ item, matches, score }): ForwardListItem => ({
      key: generateSessionKey('group', item.ID),
      type: 'group',
      groupId: item.ID,
      imageURL: item.GroupImg,
      name: item.GroupName,
      userCount: item.UserCount,
      FuzzySearch: { highlightRanges: matches, score },
    }),
  });

  const filterFriends: ForwardListItem[] = useFuseSearch({
    data: sortedFriends,
    keys: ['RemarkName', 'FriendNickName'],
    keyword: searchTerm,
    initialReturnAll: true,
    mapResultItem: ({ item, matches, score }) => ({
      key: generateSessionKey('session', item.ChatSessionID),
      type: 'user',
      userId: item.FriendID,
      imageURL: item.FriendHeadImg,
      name: item.RemarkName || item.FriendNickName,
      remarkName: item.RemarkName,
      FuzzySearch: { highlightRanges: matches, score },
    }),
  });

  useEffect(() => {
    Promise.allSettled([
      queryClient.refetchQueries(allChatSessionQueryOptions),
      queryClient.refetchQueries(friendsQueryOptions),
      queryClient.refetchQueries(groupsQueryOptions),
    ]).catch(console.error);
  }, [queryClient]);

  let options: ForwardListItem[];

  switch (type) {
    case 'recent':
      options = filteredRecentSessions;
      break;
    case 'groups':
      options = filteredGroups;
      break;
    case 'friends':
      options = filterFriends;
      break;
    case 'tags':
      options = tags.map((tag) => ({
        key: `tag-${tag.Id}`,
        type: 'tag',
        name: `${tag.TagName} (${tag.TagCount})`,
        tagId: tag.Id,
        userIds: tag.TagMember.map((member) => member.Id),
      }));
      break;
    default:
      options = [...filteredRecentSessions, ...filterFriends, ...filteredGroups];
      break;
  }

  return {
    sortBy,
    setSortBy,
    options,
    recentSessions: filteredRecentSessions,
    searchTerm,
    listType: type,
    setSearchTerm,
    setListType: setType,
  } as const;
}
